import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  Chip,
  Alert,
} from "@mui/material";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import { FriendlyButton } from "../shared";

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const ExistingPromises = ({
  t,
  promises = [],
  requestedPromiseId = "",
  onSelect,
  onCreateNew,
  error = null,
}) => {
  return (
    <Box sx={{ maxWidth: 760, mx: "auto" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <VolunteerActivismIcon sx={{ fontSize: 54, color: "primary.main", mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
          {t("your_promises", "Your Promises")}
        </Typography>
        <Typography color="text.secondary">
          {t(
            "select_unpaid_promise",
            "Select an unpaid promise to make or schedule a payment."
          )}
        </Typography>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {promises.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("no_unpaid_promises", "You have no unpaid promises for this campaign.")}
        </Alert>
      ) : (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {promises.map((promise) => {
            const isRequested = String(promise._id) === String(requestedPromiseId);
            return (
              <Grid item xs={12} sm={6} key={promise._id}>
                <Paper
                  variant="outlined"
                  sx={{
                    height: "100%",
                    p: 2.5,
                    borderRadius: 3,
                    borderWidth: isRequested ? 2 : 1,
                    borderColor: isRequested ? "primary.main" : "divider",
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {promise.campaign_name || t("promise", "Promise")}
                      </Typography>
                      <Chip
                        size="small"
                        color="warning"
                        label={t(promise.status || "in_progress", "In progress")}
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">
                        {t("promise_date", "Promise date")}
                      </Typography>
                      <Typography>
                        {promise.created_at
                          ? new Date(promise.created_at).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">
                        {t("promised_amount", "Promised")}
                      </Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {money(promise.promise_amount)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">
                        {t("given_amount", "Given")}
                      </Typography>
                      <Typography>{money(promise.given_amount)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 700 }}>
                        {t("amount_left", "Amount left")}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, color: "primary.main" }}>
                        {money(promise.remaining_amount)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t(promise.promise_type || "one_time_payment", "One-time payment")}
                      {promise.payment_frequency
                        ? ` • ${t(promise.payment_frequency, promise.payment_frequency)}`
                        : ""}
                    </Typography>
                    <FriendlyButton
                      onClick={() => onSelect(promise)}
                      endIcon={<ArrowForwardIcon />}
                      fullWidth
                    >
                      {t("pay_this_promise", "Pay this promise")}
                    </FriendlyButton>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <FriendlyButton
        variant="outlined"
        onClick={onCreateNew}
        startIcon={<AddIcon />}
        fullWidth
      >
        {t("make_new_promise", "Make a new promise")}
      </FriendlyButton>
    </Box>
  );
};

export default ExistingPromises;
